import { FC } from 'react';
import type { QualidadeReprocessoMes } from '@/types/dashboards';
import type { LinhaResumoQualidade } from '@/utils/equiplexQualidade';
import { fmtDias, fmtPct, fmtUn } from '@/utils/painelFormat';
import PainelCard from '../PainelCard';

interface Props {
  linhas: LinhaResumoQualidade[];
  selecionados: string[];
  /** Reprocesso por mês (só produto acabado, todas as máquinas); ausente esconde a coluna. */
  reprocesso?: Map<string, QualidadeReprocessoMes>;
}

const Reprocessados: FC<{ m: QualidadeReprocessoMes | undefined }> = ({ m }) => {
  if (!m || !m.lotes_reprocessados) return <>—</>;
  return (
    <span title={`${fmtUn(m.lotes_reprocessados)} de ${fmtUn(m.lotes_produzidos)} lotes produzidos`}>
      {fmtUn(m.lotes_reprocessados)}
      {m.pct_reprocessados !== null && <span className="ml-1 text-2xs text-content-muted">({fmtPct(m.pct_reprocessados)})</span>}
    </span>
  );
};

/** Mês a mês (todos os meses do snapshot, com a fila e a máquina escolhidas); os do recorte em destaque. */
const ResumoQualidadeTable: FC<Props> = ({ linhas, selecionados, reprocesso }) => (
  <PainelCard testId="qualidade-resumo" titulo="Resumo por mês" subtitulo="Pelo mês de produção (16 a 15) da entrada no CQ.">
    <div className="overflow-x-auto -mx-5 px-5">
      <table className="w-full min-w-[980px] text-sm">
        <thead>
          <tr className="border-b border-theme-border text-left text-2xs uppercase tracking-wide text-content-muted">
            <th className="py-2 pr-3 font-medium">Mês</th>
            <th className="py-2 pr-3 text-right font-medium">Lotes</th>
            <th className="py-2 pr-3 text-right font-medium">Liberados</th>
            <th className="py-2 pr-3 text-right font-medium">Aguardando</th>
            <th className="py-2 pr-3 text-right font-medium">Rejeição sem destino</th>
            <th className="py-2 pr-3 text-right font-medium" title="Ficha pendente no Datasul com o lote já fora do CQ">
              Ficha sem saldo
            </th>
            <th className="py-2 pr-3 text-right font-medium">Cancelados</th>
            <th className="py-2 pr-3 text-right font-medium">Condicionais</th>
            {reprocesso && (
              <th className="py-2 pr-3 text-right font-medium" title="Pela data de início da OP de reprocesso; todas as máquinas">
                Reprocessados
              </th>
            )}
            <th className="py-2 pr-3 text-right font-medium">Prazo médio</th>
            <th className="py-2 text-right font-medium">p90</th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {linhas.map((l) => (
            <tr
              key={l.chave}
              data-testid={`qualidade-resumo-${l.chave}`}
              className={`border-b border-theme-border ${selecionados.includes(l.chave) ? '' : 'text-content-muted'}`}
            >
              <td className="py-2 pr-3 whitespace-nowrap">
                {l.rotulo}
                {l.emAndamento && <span className="ml-1 text-2xs text-content-muted">(em andamento)</span>}
              </td>
              <td className="py-2 pr-3 text-right">{fmtUn(l.lotes)}</td>
              <td className="py-2 pr-3 text-right">{fmtUn(l.liberados)}</td>
              <td className="py-2 pr-3 text-right">{l.pendentes ? fmtUn(l.pendentes) : '—'}</td>
              <td className="py-2 pr-3 text-right">{l.rejeicaoPendente ? fmtUn(l.rejeicaoPendente) : '—'}</td>
              <td className="py-2 pr-3 text-right">{l.semSaldo ? fmtUn(l.semSaldo) : '—'}</td>
              <td className="py-2 pr-3 text-right">{l.cancelados ? fmtUn(l.cancelados) : '—'}</td>
              <td className="py-2 pr-3 text-right">{l.condicionais ? fmtUn(l.condicionais) : '—'}</td>
              {reprocesso && (
                <td className="py-2 pr-3 text-right">
                  <Reprocessados m={reprocesso.get(l.chave)} />
                </td>
              )}
              <td className="py-2 pr-3 text-right font-medium">{fmtDias(l.prazoMedio)}</td>
              <td className="py-2 text-right">{fmtDias(l.prazoP90)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </PainelCard>
);

export default ResumoQualidadeTable;
